import { ValidationResponse, JobDetailsResponse } from "../types/interview";

class ApiService {
  private baseUrl: string;

  constructor() {
    // In production, use same domain if no API URL is specified
    // In development, default to localhost:8000
    if (process.env.NODE_ENV === "production" && !process.env.REACT_APP_API_URL) {
      // Use same domain in production (for Render static site + backend)
      this.baseUrl = window.location.origin;
    } else {
      this.baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
    }

    console.log(`API Service initialized with baseUrl: ${this.baseUrl}`);
  }

  async validateApplicationId(applicationId: string): Promise<ValidationResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/salesforce/validate-application-id`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ application_id: applicationId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.message || "Validation failed");
      }

      return data;
    } catch (error) {
      console.error("Error validating Application ID:", error);
      throw error;
    }
  }

  async getJobDetails(jobId: string): Promise<JobDetailsResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/salesforce/job-details?job_id=${jobId}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.message || "Failed to fetch job details");
      }

      return data;
    } catch (error) {
      console.error("Error fetching job details:", error);
      throw error;
    }
  }

  async createReferralLead(payload: {
    name: string;
    email: string;
    company?: string;
    phone?: string;
    applicant_id?: string;
    job_name?: string;
    rating?: number;
    opportunity_discussed_id?: string;
  }): Promise<{ lead_id: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/referrals/create-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || data.message || "Failed to create referral lead");
      }
      return data;
    } catch (error) {
      console.error("Error creating referral lead:", error);
      throw error;
    }
  }

  async mintMagicLink(
    lead_id: string,
    utm?: Record<string, string>
  ): Promise<{
    lead_id: string;
    magic_link: string;
    token: string;
    compose_links: Record<string, string>;
    intro_blurb: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/referrals/magic-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_id, utm }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || data.message || "Failed to mint magic link");
      }
      return data;
    } catch (error) {
      console.error("Error minting magic link:", error);
      throw error;
    }
  }

  async markIntroSent(lead_id: string, channel: string): Promise<{ status: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/referrals/mark-intro-sent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_id, channel }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || data.message || "Failed to mark intro sent");
      }
      return data;
    } catch (error) {
      console.error("Error marking intro sent:", error);
      throw error;
    }
  }

  async sendReferralSms(
    lead_id: string
  ): Promise<{ status: string; provider: string; to: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/referrals/send-sms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_id }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || data.message || "Failed to send SMS");
      }
      return data;
    } catch (error) {
      console.error("Error sending referral SMS:", error);
      throw error;
    }
  }

  // Health check endpoint (useful for monitoring)
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/health/`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error("Health check failed");
      }

      return data;
    } catch (error) {
      console.error("Health check failed:", error);
      throw error;
    }
  }

  // Add punctuation to speech-to-text transcript
  async addPunctuation(
    text: string,
    clientId?: string
  ): Promise<{
    original_text: string;
    punctuated_text: string;
    success: boolean;
    message: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/voice/punctuation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text,
          client_id: clientId,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.message || "Failed to add punctuation");
      }

      return data;
    } catch (error) {
      console.error("Error adding punctuation:", error);
      throw error;
    }
  }
}

// Export a singleton instance
export const apiService = new ApiService();
